import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3, ChevronDown, ChevronUp, Clock, Download, MapPin,
  Play, Search, Square, Target, Zap, CheckCircle2, Loader2,
  Sparkles, UploadCloud, X
} from 'lucide-react';

const BRAZIL_STATES = [
  'Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahia', 'Ceará', 'Distrito Federal',
  'Espírito Santo', 'Goiás', 'Maranhão', 'Mato Grosso', 'Mato Grosso do Sul',
  'Minas Gerais', 'Pará', 'Paraíba', 'Paraná', 'Pernambuco', 'Piauí',
  'Rio de Janeiro', 'Rio Grande do Norte', 'Rio Grande do Sul', 'Rondônia',
  'Roraima', 'Santa Catarina', 'São Paulo', 'Sergipe', 'Tocantins',
];

export default function FlowConfig({
  // State
  isRunning, isPaused,
  searchMode, setSearchMode,
  categories, inputValue, setInputValue,
  apiKey, setApiKey,
  dripMode, setDripMode, totalGoal, setTotalGoal, hours, setHours,
  waitMinutes, setWaitMinutes,
  state, setState, country, setCountry,
  selectedStates, statePicker, setStatePicker,
  onAddState, onRemoveState, onMoveState, onSetStates,
  searchSequence, searchSequenceInput, setSearchSequenceInput,
  seqCity, setSeqCity, seqNeighborhood, setSeqNeighborhood,
  seqCoords, setSeqCoords, seqRadius, setSeqRadius,
  enterpriseType, setEnterpriseType,
  extractSocials, setExtractSocials,
  headlessMode, setHeadlessMode,
  panelDrip, setPanelDrip, panelRegion, setPanelRegion,
  panelQueue, setPanelQueue,
  status, results, agent2Running,
  // Handlers
  onStart, onStop, onPause, onResume,
  onDownload, onValidateBase,
  onAddCategory, onRemoveCategory,
  onAddSequence, onRemoveSequence, onAddSequenceItem,
  onClearDidacticFields,
}) {
  const [panelAi, setPanelAi] = useState(false);
  const [aiImage, setAiImage] = useState(null);
  const [aiImagePreview, setAiImagePreview] = useState(null);
  const [aiDesc, setAiDesc] = useState('');
  const [aiState, setAiState] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleAiGenerate = async () => {
    if (!aiImage || !aiDesc) {
      alert("Por favor, envie uma foto e uma descrição.");
      return;
    }
    
    setIsAiLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', aiImage);
      formData.append('description', aiDesc);
      formData.append('state', aiState);
      
      const res = await fetch('http://localhost:5000/ai/generate-keywords', {
        method: 'POST',
        body: formData,
      });
      
      const data = await res.json();
      if (res.ok && data.keywords && data.keywords.length > 0) {
        // Altera para a busca avançada e adiciona na fila
        setSearchMode('avancado');
        setPanelQueue(true);
        const sequenceStr = data.keywords.join(', ');
        setSearchSequenceInput((prev) => prev ? `${prev}, ${sequenceStr}` : sequenceStr);
        alert(`IA sugeriu ${data.keywords.length} palavras-chave! Elas foram adicionadas à sua fila avançada.`);
      } else {
        alert(data.detail || "Não foi possível gerar palavras-chave.");
      }
    } catch (err) {
      alert("Erro ao conectar com a IA: " + err.message);
    } finally {
      setIsAiLoading(false);
    }
  };

  return (
    <div
      className="glass card flow-config-shell expanded"
      style={{ padding: 0, position: 'relative', minHeight: 60 }}
    >
      <div className="flow-config-body" style={{ display: 'block', padding: '1.25rem' }}>
        {/* Header */}
        <div className="flow-config-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
          <BarChart3 size={20} style={{ marginRight: '8px', color: 'var(--accent)' }} />
          <span style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '1rem' }}>Fluxo</span>
        </div>

        <div className="config-section flow-config-grid" style={{ marginBottom: '1rem', display: 'grid', gridTemplateColumns: '1fr', gap: '0.85rem' }}>
          {/* ── CHAVE DE API ── */}
          <div className="setup-panel glass" style={{ padding: '1.25rem', borderRadius: '14px', background: 'var(--glass-panel-bg)', border: apiKey ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(255, 97, 97, 0.4)' }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px', fontWeight: 600 }}>CHAVE DE API (GOOGLE MAPS)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--glass-panel-border)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Zap size={16} color={apiKey ? "var(--accent)" : "var(--warning)"} />
              <input 
                type="password" 
                placeholder="Insira sua Chave de API aqui..." 
                value={apiKey} 
                onChange={(e) => {
                  setApiKey(e.target.value);
                  localStorage.setItem('leadszonemap_api_key', e.target.value);
                }} 
                style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: '0.9rem', outline: 'none' }} 
                disabled={isRunning} 
              />
            </div>
          </div>

          {/* ── GOTEJAMENTO ── */}
          <div className="setup-panel glass" style={{ padding: '1.25rem', borderRadius: '14px', background: 'var(--glass-panel-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <button
              onClick={() => setPanelDrip((v) => !v)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)' }}
            >
              <Clock size={16} color="var(--accent)" />
              <span style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', flex: 1, textAlign: 'left' }}>GOTEJAMENTO (DRIP MODE)</span>
              {panelDrip ? <ChevronUp size={16} color="var(--text-secondary)" /> : <ChevronDown size={16} color="var(--text-secondary)" />}
            </button>
            <AnimatePresence>
              {panelDrip && (
                <motion.div
                  initial={{ height: 0, opacity: 0, marginTop: 0 }}
                  animate={{ height: 'auto', opacity: 1, marginTop: '1.25rem' }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  style={{ overflow: 'hidden' }}
                >
                  <label className="drip-toggle-wrap" style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div className="drip-toggle">
                      <input type="checkbox" checked={dripMode} onChange={(e) => setDripMode(e.target.checked)} disabled={isRunning} />
                      <span className="drip-toggle__track" />
                      <span className="drip-toggle__thumb" />
                    </div>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: dripMode ? 'var(--accent)' : 'var(--text-secondary)' }}>
                      {dripMode ? 'Ativado (Modo Silencioso)' : 'Desativado'}
                    </span>
                  </label>
                  
                  <div style={{ opacity: dripMode ? 1 : 0.4, pointerEvents: dripMode ? 'auto' : 'none', transition: 'opacity 0.3s', display: 'grid', gap: '1rem', background: 'var(--glass-element-bg)', padding: '1rem', borderRadius: '10px' }}>
                    <div>
                      <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px', fontWeight: 600 }}>META DE LEADS</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--glass-panel-border)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <Target size={16} color="var(--text-secondary)" />
                        <input type="number" value={totalGoal} onChange={(e) => setTotalGoal(e.target.value)} style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: '0.9rem', outline: 'none' }} disabled={!dripMode} />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px', fontWeight: 600 }}>DURAÇÃO (HORAS)</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--glass-panel-border)', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <Clock size={16} color="var(--text-secondary)" />
                        <input type="number" value={hours} onChange={(e) => setHours(e.target.value)} style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: '0.9rem', outline: 'none' }} disabled={!dripMode} />
                      </div>
                    </div>
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '1rem' }}>
                      <label style={{ fontSize: '0.7rem', color: 'var(--accent)', fontWeight: 700, display: 'block', marginBottom: '8px' }}>INTERVALO FIXO (OPCIONAL)</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(116, 242, 255, 0.05)', padding: '8px 12px', borderRadius: '8px', border: waitMinutes && waitMinutes < 2 ? '1px solid #e74c3c' : '1px solid rgba(116, 242, 255, 0.2)' }}>
                        <Zap size={16} color="var(--accent)" />
                        <input
                          type="number"
                          placeholder="Minutos (Min: 2)"
                          value={waitMinutes}
                          onChange={(e) => setWaitMinutes(e.target.value)}
                          disabled={!dripMode}
                          style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: '0.9rem', outline: 'none' }}
                        />
                      </div>
                      {waitMinutes && waitMinutes < 2 && (
                        <div style={{ fontSize: '0.7rem', color: '#e74c3c', marginTop: '6px' }}>O intervalo mínimo recomendado é de 2 minutos para evitar bloqueios.</div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── REGIÃO E CIDADES ── */}
          <div className="setup-panel glass" style={{ padding: '1.25rem', borderRadius: '14px', background: 'var(--glass-panel-bg)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <button
              onClick={() => setPanelRegion((v) => !v)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)' }}
            >
              <MapPin size={16} color="var(--accent)" />
              <span style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', flex: 1, textAlign: 'left' }}>REGIÃO E CIDADES</span>
              {panelRegion ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            <AnimatePresence>
              {panelRegion && (
                <motion.div
                  initial={{ height: 0, opacity: 0, marginTop: 0 }}
                  animate={{ height: 'auto', opacity: 1, marginTop: '1.25rem' }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div style={{ background: 'var(--glass-panel-bg)', padding: '1rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '10px', letterSpacing: '0.04em', fontWeight: 600 }}>CADEIA DE ESTADOS (RODÍZIO)</label>
                      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                        <select value={statePicker} onChange={(e) => setStatePicker(e.target.value)} disabled={isRunning} className="glass" style={{ flex: 1, padding: '10px', borderRadius: '8px', background: 'var(--glass-element-bg)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', outline: 'none' }}>
                          <option value="">Selecione um estado...</option>
                          {BRAZIL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button className="client-btn" onClick={onAddState} disabled={isRunning || !statePicker} style={{ padding: '0 20px', borderRadius: '8px' }}>Adicionar</button>
                        <button className="client-btn client-btn--outline" onClick={() => onSetStates(BRAZIL_STATES)} disabled={isRunning} style={{ padding: '0 15px', borderRadius: '8px' }}>Todos</button>
                        <button className="client-btn client-btn--outline" onClick={() => onSetStates([])} disabled={isRunning || selectedStates.length === 0} style={{ padding: '0 15px', borderRadius: '8px' }}>Limpar</button>
                      </div>
                      <div style={{ maxHeight: '150px', overflowY: 'auto', background: 'var(--glass-element-bg)', border: '1px solid rgba(255,255,255,0.05)', padding: '8px', borderRadius: '8px' }}>
                        {selectedStates.length === 0 ? (
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', textAlign: 'center', padding: '1rem' }}>Nenhum estado. Busca livre.</div>
                        ) : selectedStates.map((s, idx) => (
                          <div key={s} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', background: 'var(--glass-element-bg)', marginBottom: '4px', borderRadius: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'var(--accent)', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700 }}>{idx + 1}</div>
                              <span style={{ fontWeight: 500 }}>{s}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }} onClick={() => onMoveState(idx, -1)} disabled={isRunning || idx === 0}>↑</button>
                              <button style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }} onClick={() => onMoveState(idx, 1)} disabled={isRunning || idx === selectedStates.length - 1}>↓</button>
                              <button style={{ background: 'transparent', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }} onClick={() => onRemoveState(idx)} disabled={isRunning}>✕</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── ASSISTENTE DE IA ── */}
          <div className="setup-panel glass" style={{ padding: '1.25rem', borderRadius: '14px', background: 'var(--glass-panel-bg)', border: '1px solid rgba(255,107,181,0.3)', marginBottom: '0.4rem', boxShadow: '0 0 10px rgba(255,107,181,0.1)' }}>
            <button
              onClick={() => setPanelAi((v) => !v)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)' }}
            >
              <Sparkles size={16} color="#ff6bb5" />
              <span style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', flex: 1, textAlign: 'left', color: '#ff6bb5' }}>ASSISTENTE IA (GERADOR DE ALVOS)</span>
              {panelAi ? <ChevronUp size={16} color="#ff6bb5" /> : <ChevronDown size={16} color="#ff6bb5" />}
            </button>
            <AnimatePresence>
              {panelAi && (
                <motion.div
                  initial={{ height: 0, opacity: 0, marginTop: 0 }}
                  animate={{ height: 'auto', opacity: 1, marginTop: '1.25rem' }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  style={{ overflow: 'hidden' }}
                >
                  <div style={{ background: 'var(--glass-panel-bg)', padding: '1rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>1. FOTO DO SEU PRODUTO/EMPRESA</label>
                      <input 
                        type="file" 
                        accept="image/*" 
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setAiImage(file);
                            setAiImagePreview(URL.createObjectURL(file));
                          }
                        }}
                      />
                      {!aiImagePreview ? (
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          style={{ border: '2px dashed rgba(255,255,255,0.1)', borderRadius: '8px', padding: '2rem', textAlign: 'center', cursor: 'pointer', background: 'var(--glass-element-bg)' }}
                        >
                          <UploadCloud size={24} color="var(--text-secondary)" style={{ margin: '0 auto 8px' }} />
                          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Clique para anexar foto</p>
                        </div>
                      ) : (
                        <div style={{ position: 'relative', display: 'inline-block', borderRadius: '8px', overflow: 'hidden' }}>
                          <img src={aiImagePreview} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                          <button 
                            onClick={() => { setAiImage(null); setAiImagePreview(null); }}
                            style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', padding: '4px', cursor: 'pointer', color: '#fff' }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>2. O QUE VOCÊ VENDE? (DESCRIÇÃO)</label>
                      <textarea 
                        value={aiDesc}
                        onChange={(e) => setAiDesc(e.target.value)}
                        placeholder="Ex: Somos distribuidores atacadistas de grãos de café especiais, atendendo a região sul."
                        rows={3}
                        className="glass"
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', background: 'var(--glass-element-bg)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', outline: 'none', resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ marginBottom: '1.25rem' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>3. ESTADO ALVO (OPCIONAL)</label>
                      <select value={aiState} onChange={(e) => setAiState(e.target.value)} className="glass" style={{ width: '100%', padding: '10px', borderRadius: '8px', background: 'var(--glass-element-bg)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', outline: 'none' }}>
                        <option value="">Brasil (Qualquer lugar)</option>
                        {BRAZIL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <button 
                      className="client-btn" 
                      onClick={handleAiGenerate}
                      disabled={isAiLoading || isRunning}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'linear-gradient(90deg, #ff6bb5 0%, #ff8f71 100%)', border: 'none' }}
                    >
                      {isAiLoading ? <Loader2 size={16} className="spin" /> : <Sparkles size={16} />}
                      {isAiLoading ? 'Analisando com Inteligência Artificial...' : 'GERAR ALVOS COM IA'}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Socials Toggle */}
          <div className="setup-panel glass" style={{ borderRadius: '14px', border: '1px solid rgba(116,242,255,0.1)', overflow: 'hidden', padding: '1.25rem', marginBottom: '0.4rem', background: 'var(--glass-panel-bg)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <div className="drip-toggle">
                <input type="checkbox" checked={extractSocials} onChange={(e) => setExtractSocials(e.target.checked)} disabled={isRunning && !isPaused} />
                <span className="drip-toggle__track" />
                <span className="drip-toggle__thumb" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: extractSocials ? 'var(--accent)' : 'var(--text-primary)' }}>
                  Extração Profunda (Redes Sociais e WhatsApp do Site)
                </span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                  Puxar Instagram, Facebook, LinkedIn e checar site (mais lento)
                </span>
              </div>
            </label>
          </div>

          {/* Headless Toggle */}
          <div className="setup-panel glass" style={{ borderRadius: '14px', border: '1px solid rgba(116,242,255,0.1)', overflow: 'hidden', padding: '1.25rem', marginBottom: '0.4rem', background: 'var(--glass-panel-bg)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <div className="drip-toggle">
                <input type="checkbox" checked={headlessMode} onChange={(e) => setHeadlessMode(e.target.checked)} disabled={isRunning && !isPaused} />
                <span className="drip-toggle__track" />
                <span className="drip-toggle__thumb" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: headlessMode ? 'var(--accent)' : 'var(--text-primary)' }}>
                  Modo Furtivo / Invisível
                </span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                  Ocultar as janelas do navegador durante a raspagem
                </span>
              </div>
            </label>
          </div>

          {/* ── BUSCA: FILA / AVANÇADO ── */}
          <div className="setup-panel glass" style={{ padding: '1.25rem', borderRadius: '14px', background: 'var(--glass-panel-bg)', border: '1px solid rgba(255,255,255,0.05)', marginBottom: '0.4rem' }}>
            <button
              onClick={() => setPanelQueue((v) => !v)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)' }}
            >
              <Search size={16} color="var(--accent)" />
              <span style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', flex: 1, textAlign: 'left' }}>
                ALVOS ({searchMode === 'fila' ? 'FILA' : 'AVANÇADO'})
              </span>
              {panelQueue ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>

            <AnimatePresence>
              {panelQueue && (
                <motion.div
                  initial={{ height: 0, opacity: 0, marginTop: 0 }}
                  animate={{ height: 'auto', opacity: 1, marginTop: '1.25rem' }}
                  exit={{ height: 0, opacity: 0, marginTop: 0 }}
                  style={{ overflow: 'hidden' }}
                >
                  {/* Toggle */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '1.25rem', background: 'var(--glass-element-bg)', borderRadius: '10px', padding: '6px' }}>
                    {['fila', 'avancado'].map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setSearchMode(mode)}
                        disabled={isRunning}
                        style={{
                          flex: 1, padding: '10px', fontSize: '0.8rem', fontWeight: searchMode === mode ? 700 : 500,
                          letterSpacing: '0.04em', borderRadius: '8px', border: 'none', cursor: 'pointer',
                          background: searchMode === mode ? 'var(--glass-panel-border)' : 'transparent',
                          color: searchMode === mode ? 'var(--text-primary)' : 'var(--text-muted)',
                          transition: 'all 0.2s',
                        }}
                      >
                        {mode === 'fila' ? 'FILA DE TERMOS' : 'BUSCA AVANÇADA'}
                      </button>
                    ))}
                  </div>

                  {/* Modo Fila */}
                  {searchMode === 'fila' && (
                    <div style={{ background: 'var(--glass-panel-bg)', padding: '1rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                        Insira os termos de busca (ex: <em>Restaurante</em>, <em>Posto de Gasolina</em>).
                      </div>
                      <input
                        type="text"
                        placeholder="Pressione Enter para adicionar..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={onAddCategory}
                        disabled={isRunning}
                        style={{ width: '100%', padding: '12px', background: 'var(--glass-element-bg)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '8px', outline: 'none' }}
                      />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '1rem' }}>
                        {categories.map((cat, i) => (
                          <span key={i} style={{ padding: '6px 12px', fontSize: '0.85rem', borderRadius: '20px', background: 'rgba(116, 242, 255, 0.1)', border: '1px solid rgba(116, 242, 255, 0.2)', color: '#d9e7ff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {cat}
                            {!isRunning && <button onClick={() => onRemoveCategory(i)} style={{ background: 'none', border: 'none', color: '#e74c3c', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>×</button>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Modo Avançado */}
                  {searchMode === 'avancado' && (
                    <div style={{ background: 'var(--glass-panel-bg)', padding: '1rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                        Combine parâmetros geoespaciais e de precisão extrema.
                      </div>
                      <div className="sequence-rows" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 108px', gap: '10px', marginBottom: '12px' }}>
                        {[
                          { label: 'Cidade', val: seqCity, set: setSeqCity, key: 'city' },
                          { label: 'Bairro', val: seqNeighborhood, set: setSeqNeighborhood, key: 'bairro' },
                          { label: 'Coords', val: seqCoords, set: setSeqCoords, key: 'coords', placeholder: 'Coordenadas lat,lng' },
                          { label: 'Raio', val: seqRadius, set: setSeqRadius, key: 'radius', placeholder: 'Raio (metros)' },
                        ].map(({ label, val, set, key, placeholder }) => (
                          <React.Fragment key={key}>
                            <input placeholder={placeholder || label} value={val} onChange={(e) => set(e.target.value)} disabled={isRunning} style={{ padding: '10px 12px', background: 'var(--glass-element-bg)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '8px', outline: 'none' }} />
                            <button className="client-btn client-btn--ghost" disabled={isRunning} onClick={() => { onAddSequenceItem(key, val); set(''); }} style={{ padding: '10px', minWidth: '108px', borderRadius: '8px' }}>
                              + {label}
                            </button>
                          </React.Fragment>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                        <input type="text" placeholder="Texto livre (ex: Shopping)" value={searchSequenceInput} onChange={(e) => setSearchSequenceInput(e.target.value)} onKeyDown={onAddSequence} disabled={isRunning} style={{ padding: '10px 12px', background: 'var(--glass-element-bg)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-primary)', borderRadius: '8px', outline: 'none', flex: 1 }} />
                        <button className="client-btn" disabled={isRunning} onClick={() => { if (searchSequenceInput.trim()) { onAddSequenceItem(null, searchSequenceInput); setSearchSequenceInput(''); } }} style={{ padding: '10px 20px', borderRadius: '8px' }}>
                          Adicionar
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '1rem' }}>
                        {searchSequence.map((s, i) => (
                          <span key={i} style={{ padding: '6px 12px', fontSize: '0.85rem', borderRadius: '20px', background: 'rgba(216, 255, 62, 0.1)', border: '1px solid rgba(216, 255, 62, 0.2)', color: '#f0f3db', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {s}
                            {!isRunning && <button onClick={() => onRemoveSequence(i)} style={{ background: 'none', border: 'none', color: '#e74c3c', fontWeight: 700, cursor: 'pointer' }}>×</button>}
                          </span>
                        ))}
                      </div>
                      <button className="client-btn client-btn--ghost" disabled={isRunning} onClick={onClearDidacticFields} style={{ marginTop: '1rem', width: '100%', borderColor: 'rgba(231, 76, 60, 0.3)', color: '#e74c3c' }}>
                        Limpar tudo
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ── Actions ── */}
        <div className="flow-config-actions" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px', marginTop: '1rem' }}>
          {!isRunning ? (
            <button
              className="client-btn"
              onClick={onStart}
              disabled={searchMode === 'fila' ? categories.length === 0 : searchSequence.length === 0}
              style={{ padding: '18px 24px', fontSize: '1.1rem', fontWeight: 800, gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', borderRadius: '12px', letterSpacing: '0.05em' }}
            >
              <Play size={22} fill="currentColor" /> INICIAR FLUXO DA MÁQUINA
            </button>
          ) : (
            <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {isPaused ? (
                <button className="client-btn" onClick={onResume} style={{ background: '#2ecc71', padding: '18px 24px', fontSize: '1.1rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', borderRadius: '12px', letterSpacing: '0.05em', color: '#000' }}>
                  <Play size={22} fill="currentColor" /> RETOMAR OPERAÇÃO
                </button>
              ) : (
                <button className="client-btn" onClick={onPause} style={{ background: '#f39c12', padding: '18px 24px', fontSize: '1.1rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', borderRadius: '12px', letterSpacing: '0.05em', color: '#000' }}>
                  <Square size={22} fill="currentColor" /> PAUSAR OPERAÇÃO
                </button>
              )}
              <button className="client-btn" onClick={onStop} style={{ background: '#e74c3c', padding: '18px 24px', fontSize: '1.1rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', borderRadius: '12px', letterSpacing: '0.05em' }}>
                <Square size={22} fill="currentColor" /> ABORTAR
              </button>
            </div>
          )}
          <button className="client-btn client-btn--ghost" onClick={onDownload} disabled={results.length === 0} style={{ padding: '14px', borderRadius: '10px' }}>
            <Download size={18} /> EXPORTAR HOJE ({status.results_count})
          </button>
          <button className="client-btn client-btn--ghost" onClick={onValidateBase} disabled={agent2Running} style={{ padding: '14px', borderRadius: '10px', borderColor: 'rgba(116,242,255,0.2)', color: 'var(--accent)' }}>
            {agent2Running ? <Loader2 size={18} className="spin" /> : <CheckCircle2 size={18} />} VALIDAR BASE
          </button>
        </div>
      </div>
    </div>
  );
}
